import { UserLanguage } from '../../../../../features/account/types/User'
import { InternalFeatureMap } from '../../../../api/features'
import { LanguageMaps } from '../../../../api/language'
import { ContentLanguageTranslation } from '../../../../types/InternalAnalystComment'
import { InternalConcept, InternalTag, InternalTagImplementations, InternalTagOrder, InternalTagSaveBody, InternalTagType } from '../../../../types/InternalTag'

export const tagLanguages: UserLanguage[] = ['en', 'ja', 'zh']

export const tagTypes: InternalTagType[] = [
  InternalTagType.Feature,
  InternalTagType.Game,
  InternalTagType.Implementation,
  InternalTagType.Chapter,
  InternalTagType.Brand,
  InternalTagType.Event,
  InternalTagType.EventSecondary,
  InternalTagType.Concept,
  InternalTagType.Report,
]

export const tagTypeLabelKeys: Record<InternalTagType, string> = {
  [InternalTagType.Feature]: 'internal-service-management:tags_type_feature',
  [InternalTagType.Game]: 'internal-service-management:tags_type_game',
  [InternalTagType.Implementation]: 'internal-service-management:tags_type_implementation',
  [InternalTagType.Chapter]: 'internal-service-management:tags_type_chapter',
  [InternalTagType.Brand]: 'internal-service-management:tags_type_brand',
  [InternalTagType.Event]: 'internal-service-management:tags_type_event',
  [InternalTagType.EventSecondary]: 'internal-service-management:tags_type_event_secondary',
  [InternalTagType.Concept]: 'internal-service-management:tags_type_concept',
  [InternalTagType.Report]: 'internal-service-management:tags_type_report',
}

export const isTagType = (value: string | null): value is InternalTagType => tagTypes.includes(value as InternalTagType)

// Feature tags target features; game, chapter and brand tags target feature choices.
const choiceTargetTypes = [InternalTagType.Game, InternalTagType.Chapter, InternalTagType.Brand]

export const hasFeatureColumn = (type: InternalTagType) => type === InternalTagType.Feature || type === InternalTagType.Implementation
export const hasChoiceColumn = (type: InternalTagType) => choiceTargetTypes.includes(type)
export const hasGamesColumn = (type: InternalTagType) => type === InternalTagType.Implementation
export const hasConceptColumns = (type: InternalTagType) => type === InternalTagType.Concept
export const isAssignableTagType = (type: InternalTagType) => type === InternalTagType.Feature || type === InternalTagType.Game

export type TagRow = {
  tag: InternalTag
  // Features (feature and implementation tags) or "Feature - Choice" names (game, chapter and brand tags) in the market.
  assigned?: string[]
  games?: string[]
  featureTagId?: string | null
  featureTagName?: string
  // Undefined when no concept holds the tag.
  screenshotCount?: number
}

export type TagAssignmentData = {
  featureMap?: InternalFeatureMap
  implementations?: InternalTagImplementations
  concepts?: InternalConcept[]
}

const buildChoiceNames = (featureMap: InternalFeatureMap) => {
  const names = new Map<number, string>()
  Object.values(featureMap).forEach((feature) => feature.choices?.forEach((choice) => names.set(choice.weightId, `${feature.name} - ${choice.choice}`)))
  return names
}

const getAssignedNames = (
  tag: InternalTag,
  data: TagAssignmentData,
  featureNames?: Map<number, string>,
  choiceNames?: Map<number, string>
): string[] | undefined => {
  if (tag.type === InternalTagType.Implementation) return data.implementations ? data.implementations.features[tag.id] ?? [] : undefined
  const names = tag.type === InternalTagType.Feature ? featureNames : hasChoiceColumn(tag.type) ? choiceNames : undefined
  return names && (tag.targets ?? []).flatMap((target) => names.get(target) ?? [])
}

// Tags of one type in stored order. Most tags share order 0 and the API lists them in a different order on each read,
// so equal orders are sorted by name.
export const buildTagRows = (tags: InternalTag[], type: InternalTagType, data: TagAssignmentData): TagRow[] => {
  const featureNames = data.featureMap && new Map(Object.values(data.featureMap).map((feature) => [feature.legacyId, feature.name]))
  const choiceNames = data.featureMap && buildChoiceNames(data.featureMap)
  const tagNames = new Map(tags.map((tag) => [tag.id, tag.name]))

  return tags
    .filter((tag) => tag.type === type)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name))
    .map((tag) => {
      const row: TagRow = { tag, assigned: getAssignedNames(tag, data, featureNames, choiceNames) }
      if (tag.type === InternalTagType.Implementation && data.implementations) row.games = data.implementations.games[tag.id] ?? []
      if (tag.type === InternalTagType.Concept && data.concepts) {
        const concepts = data.concepts.filter((concept) => concept.tags.includes(tag.id))
        const featureTagId = concepts[0]?.featureTagId ?? null
        row.featureTagId = featureTagId
        row.featureTagName = featureTagId ? tagNames.get(featureTagId) : undefined
        row.screenshotCount = concepts.length ? concepts.reduce((sum, concept) => sum + concept.screenshotIds.length, 0) : undefined
      }
      return row
    })
}

export const allFeatureTags = 'all'

export const filterTagRows = (rows: TagRow[], search: string, featureTagId: string = allFeatureTags): TagRow[] => {
  const needle = search.trim().toLowerCase()
  return rows.filter(
    (row) => (!needle || row.tag.name.toLowerCase().includes(needle)) && (featureTagId === allFeatureTags || row.featureTagId === featureTagId)
  )
}

// Legacy numbers every tag of the type from 1 in the shown order.
export const toTagOrder = (rows: TagRow[]): InternalTagOrder => Object.fromEntries(rows.map((row, index) => [row.tag.id, index + 1]))

export const getFeatureTags = (tags: InternalTag[]): InternalTag[] =>
  tags.filter((tag) => tag.type === InternalTagType.Feature).sort((a, b) => a.name.localeCompare(b.name))

export const getConceptForTag = (concepts: InternalConcept[] | undefined, tagId?: string) =>
  tagId ? concepts?.find((concept) => concept.tags.includes(tagId)) : undefined

export type TagFormValues = {
  names: ContentLanguageTranslation
  descriptions: ContentLanguageTranslation
  imageTexts: ContentLanguageTranslation
  imageUrl: string
  type: InternalTagType
  active: boolean
  // '' stands for no feature tag.
  featureTagId: string
}

type TranslatedField = 'name' | 'description' | 'imageText'

const getTranslations = (
  tag: InternalTag | undefined,
  languageMaps: LanguageMaps | undefined,
  field: TranslatedField,
  storedEn?: string | null
): ContentLanguageTranslation => {
  if (!tag) return {}
  return Object.fromEntries(
    tagLanguages.map((language) => {
      const translated: string | undefined = languageMaps?.[language]?.tags?.[tag.id]?.[field]
      return [language, language === 'en' ? translated || storedEn || '' : translated ?? '']
    })
  )
}

export const toTagFormValues = (
  tag: InternalTag | undefined,
  languageMaps: LanguageMaps | undefined,
  concept: InternalConcept | undefined,
  defaultType: InternalTagType
): TagFormValues => ({
  names: getTranslations(tag, languageMaps, 'name', tag?.name),
  descriptions: getTranslations(tag, languageMaps, 'description', tag?.description),
  imageTexts: getTranslations(tag, languageMaps, 'imageText', tag?.imageText),
  imageUrl: tag?.imageUrl ?? '',
  type: tag?.type ?? defaultType,
  active: tag?.active ?? false,
  featureTagId: concept?.featureTagId ?? '',
})

const textOrNull = (value?: string) => (value?.trim() ? value : null)

export const toTagSaveBody = (original: InternalTag | undefined, values: TagFormValues, concept: InternalConcept | undefined): InternalTagSaveBody => {
  // Legacy leaves `names` out of save bodies; the backend answers it as null.
  const stored: Partial<InternalTag> = { ...original }
  delete stored.names
  const typeChanged = !!original && original.type !== values.type
  const body: InternalTagSaveBody = {
    tag: {
      ...stored,
      name: values.names.en ?? '',
      type: values.type,
      active: values.active,
      description: textOrNull(values.descriptions.en),
      imageUrl: values.imageUrl || null,
      imageText: textOrNull(values.imageTexts.en),
      ...(typeChanged ? { targets: [] } : {}),
      translations: {
        en: { name: values.names.en ?? '', description: values.descriptions.en ?? '', imageText: values.imageTexts.en ?? '' },
        ja: { name: values.names.ja ?? '', description: values.descriptions.ja ?? '', imageText: values.imageTexts.ja ?? '' },
        zh: { name: values.names.zh ?? '', description: values.descriptions.zh ?? '', imageText: values.imageTexts.zh ?? '' },
      },
    },
  }
  if (values.type === InternalTagType.Concept) {
    const featureTagId = values.featureTagId || null
    body.concept = concept ? { ...concept, featureTagId } : { featureTagId }
  }
  return body
}

// `translations: null` leaves the language maps as they are.
export const toActiveToggleBody = (tag: InternalTag): InternalTagSaveBody => ({ tag: { ...tag, active: !tag.active, translations: null } })

export type TagField = 'name' | 'type' | 'active' | 'description' | 'imageUrl' | 'imageText' | 'targets' | 'featureTag'

export const tagFieldLabelKeys: Record<TagField, string> = {
  name: 'internal-service-management:tags_name',
  type: 'internal-service-management:tags_type',
  active: 'internal-service-management:tags_active',
  description: 'internal-service-management:tags_description',
  imageUrl: 'internal-service-management:tags_image',
  imageText: 'internal-service-management:tags_image_text',
  targets: 'internal-service-management:tags_targets',
  featureTag: 'internal-service-management:tags_feature_tag',
}

const nullableTagFields = ['description', 'imageUrl', 'imageText'] as const

// The stored tag the backend answers is compared with what was sent, since a 2xx alone doesn't prove the write landed.
export const getUnstoredTagFields = (sent: InternalTagSaveBody['tag'], stored: InternalTag): TagField[] => {
  const unstored: TagField[] = []
  if (stored.name !== sent.name) unstored.push('name')
  if (stored.type !== sent.type) unstored.push('type')
  if (!!stored.active !== !!sent.active) unstored.push('active')
  nullableTagFields.forEach((field) => {
    if ((stored[field] ?? null) !== (sent[field] ?? null)) unstored.push(field)
  })
  if (sent.targets && (stored.targets ?? []).join() !== sent.targets.join()) unstored.push('targets')
  return unstored
}

// The save answers only the tag, so the concept is read back from the refetched concepts.
export const isConceptStored = (concepts: InternalConcept[], tagId: string, featureTagId: string | null) =>
  (getConceptForTag(concepts, tagId)?.featureTagId ?? null) === featureTagId
